/**
 * Root ID Utility
 */

import { FormEntityProps } from '../types';

const LPE_ENVIRONMENTS = ['test', 'stag', 'prod'];

interface GetRootIdResult {
  data: FormEntityProps;
  rootIdName: string;
}

/**
 * Determines the root element ID and sets it if needed for standalone mode.
 *
 * In LPE environments (test, stag, prod), the root ID is constructed from
 * the form UUID and environment. In standalone mode, it falls back to 'root'
 * and attempts to set the ID attribute on the root element.
 *
 * @param formData - The form entity data containing uuid and appEnvironment
 * @returns Object containing the form data and determined root ID name
 *
 * @example
 * const { data, rootIdName } = getRootId(formData);
 * const root = document.getElementById(rootIdName);
 */
export const getRootId = (formData: FormEntityProps): GetRootIdResult => {
  const appEnvironment = formData.appEnvironment || '';
  const isLpe = LPE_ENVIRONMENTS.includes(appEnvironment);

  const rootIdName =
    formData.appEnvironment && formData.uuid
      ? `${formData.uuid}-${formData.appEnvironment.substring(0, 4)}`
      : 'root';

  // Add ID attribute for standalone (non-LPE) environments
  if (!isLpe && typeof document !== 'undefined') {
    const rootElem = document.querySelector('[name="root"]');
    if (rootElem) {
      rootElem.setAttribute('id', rootIdName);
    }
  }

  return { data: formData, rootIdName };
};
